import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { buildArticleQueryParams, useArticlesQuery, useArticleMutations, usePrefetchNextPage } from './useArticlesQuery';
import { useTagSettings } from '../../context/TagSettingsContext';
import { useBrand } from '../../context/BrandContext';
import useArticleCRUD from '../operations/useArticleCRUD';
import api from '../../services/api';
import { QUERY_KEYS } from '../../config/queryClient';
import { useLocation } from 'react-router-dom';

// Cache global em memória: preserva EXATAMENTE o estado da listagem (incluindo as centenas de cards abertos) 
// quando o componente desmonta ao mudar de página.
const globalArticlesMemoryCache = new Map();

/**
 * Hook de listagem de artigos com React Query
 * 
 * BENEFÍCIOS:
 * - Cache inteligente de 5 minutos
 * - Optimistic updates instantâneos
 * - Prefetching automático
 * - Mantém compatibilidade com código existente
 * 
 * USO:
 * Mantém a interface esperada pela tela de artigos, mas com cache e prefetch.
 */
const useArticleFetcherWithCache = ({ tagId, collectionId, initialFilters = {}, initialTimePeriod } = {}) => {
  const { tags: contextTags, fetchTags: fetchGlobalTags } = useTagSettings();
  const { brandVersion } = useBrand();
  const queryClient = useQueryClient();
  const location = useLocation();

  const sessionKey = `m_art_page_v2_${brandVersion}_${tagId || 'all'}_${collectionId || 'all'}_${location.pathname}`;

  const [page, setPage] = useState(() => {
    const cached = globalArticlesMemoryCache.get(sessionKey);
    if (cached && Date.now() - cached.timestamp < 60 * 60 * 1000) {
      return cached.page || 1;
    }
    return 1;
  });
  const sectionKey = `${location.pathname}_${tagId || 'all'}_${collectionId || 'all'}`;
  // Chave adicional para forçar re-busca quando dados externos mudam (ex: tags de coleção)
  const [externalRefreshKey, setExternalRefreshKey] = useState(0);
  const [timePeriod, setTimePeriod] = useState(() => {
    if (initialTimePeriod) return initialTimePeriod;
    const saved = localStorage.getItem('article_timePeriod');
    if (saved) return saved;
    return (tagId || collectionId) ? 'all' : '24h';
  });
  const [filters, setFilters] = useState(() => {
    // Restaurar datas personalizadas do localStorage se existirem
    const savedStartDate = localStorage.getItem('article_startDate');
    const savedEndDate = localStorage.getItem('article_endDate');

    return {
      ...initialFilters,
      startDate: savedStartDate ? new Date(savedStartDate) : initialFilters.startDate,
      endDate: savedEndDate ? new Date(savedEndDate) : initialFilters.endDate,
    };
  });
  const [sortBy, setSortByState] = useState(() => localStorage.getItem('articles_sortBy') || 'date_desc');
  const setSortBy = (value) => {
    localStorage.setItem('articles_sortBy', value);
    setSortByState(value);
  };
  const [allArticles, setAllArticles] = useState(() => {
    const cached = globalArticlesMemoryCache.get(sessionKey);
    if (cached && Date.now() - cached.timestamp < 60 * 60 * 1000) {
      return cached.articles || [];
    }
    return [];
  });
  const [allSourceOptions, setAllSourceOptions] = useState([]);

  const removeArticlesFromCache = useCallback((articleIds) => {
    const ids = Array.isArray(articleIds) ? articleIds : [articleIds];
    const idsToRemove = new Set(ids.filter(Boolean));

    if (idsToRemove.size === 0) {
      return;
    }

    setAllArticles(prevArticles =>
      prevArticles.filter(article => !idsToRemove.has(article._id))
    );

    globalArticlesMemoryCache.forEach((cached, key) => {
      if (!Array.isArray(cached?.articles)) {
        return;
      }

      globalArticlesMemoryCache.set(key, {
        ...cached,
        articles: cached.articles.filter(article => !idsToRemove.has(article._id)),
        timestamp: Date.now()
      });
    });

    queryClient.setQueriesData({ queryKey: [QUERY_KEYS.articles] }, (old) => {
      if (!old?.data || !Array.isArray(old.data)) {
        return old;
      }

      const nextData = old.data.filter(article => !idsToRemove.has(article._id));
      const removedCount = old.data.length - nextData.length;

      if (removedCount === 0) {
        return old;
      }

      return {
        ...old,
        data: nextData,
        total: typeof old.total === 'number' ? Math.max(0, old.total - removedCount) : old.total,
        totalCount: typeof old.totalCount === 'number' ? Math.max(0, old.totalCount - removedCount) : old.totalCount
      };
    });

    idsToRemove.forEach(id => {
      queryClient.removeQueries({ queryKey: QUERY_KEYS.article(id), exact: true });
    });

    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.articles], refetchType: 'active' });
  }, [queryClient]);

  // React Query para buscar artigos (com cache!)
  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch
  } = useArticlesQuery({
    page,
    limit: 60,
    sort: sortBy,
    search: filters.search,
    sentiment: filters.sentiment,
    exposure: filters.exposure,
    isRead: filters.isRead,
    isFavorite: filters.isFavorite,
    tags: filters.tags,
    hasTags: filters.hasTags,
    // Suporta tanto 'siteName' (enviado pelo template) quanto 'sources' (nome interno)
    sources: filters.siteName || filters.sources,
    // Suporta tanto 'mediaType' (enviado pelo template) quanto 'mediaTypes' (nome interno)
    mediaTypes: filters.mediaType || filters.mediaTypes,
    // Suporta tanto 'contentType' (enviado pelo template) quanto 'contentTypes' (nome interno)
    contentTypes: filters.contentType || filters.contentTypes,
    entityType: filters.entityType,
    timePeriod,
    startDate: filters.startDate,
    endDate: filters.endDate,
    dateType: filters.dateType,
    tagId,
    collectionId,
    // Suporta tanto 'tier' (enviado pelo template) quanto 'tiers' (nome interno)
    tiers: filters.tier || filters.tiers,
    editorialOccupancyStatus: filters.editorialOccupancyStatus,
    qualitativeAnalysisStatus: filters.qualitativeAnalysisStatus,
    sectionKey, // Inclui sectionKey para forçar re-busca ao trocar de seção
    externalRefreshKey, // Inclui para forçar re-busca quando dados externos mudam
  });

  // Estado para Radar Silencioso (polling de novos artigos)
  const [hasNewArticles, setHasNewArticles] = useState(false);

  // Efeito de Polling (Radar Silencioso) a cada 3 minutos
  useEffect(() => {
    // Só inicia o radar se já tivermos dados na tela
    if (!allArticles || allArticles.length === 0) return;

    let isMounted = true;
    
    const checkForNewArticles = async () => {
      try {
        // Usa as chaves do React Query para verificar se tem novidade (limit=1)
        const params = {
          page: 1,
          limit: 1, // Traz apenas o mais novo
          sort: sortBy,
          search: filters.search,
          sentiment: filters.sentiment,
          exposure: filters.exposure,
          isRead: filters.isRead,
          isFavorite: filters.isFavorite,
          tags: filters.tags,
          hasTags: filters.hasTags,
          sources: filters.sources,
          mediaTypes: filters.mediaTypes,
          contentTypes: filters.contentTypes,
          entityType: filters.entityType,
          timePeriod,
          startDate: filters.startDate,
          endDate: filters.endDate,
          dateType: filters.dateType,
          tagId,
          collectionId,
          tiers: filters.tier || filters.tiers,
          editorialOccupancyStatus: filters.editorialOccupancyStatus,
          qualitativeAnalysisStatus: filters.qualitativeAnalysisStatus,
        };

        // Usa a mesma serialização da consulta visível, inclusive período
        // personalizado e tipo de data.
        const queryParams = buildArticleQueryParams(params);

        // Faz uma requisição stealth ao backend
        const res = await api.get(`/articles?${queryParams}`);
        
        if (!isMounted) return;

        const latestArticle = res.data.data?.[0];
        const currentTotal = res.data.totalCount;
        const myTotalCount = data?.totalCount || allArticles.length;

        if (latestArticle) {
          // Se o artigo mais novo recebido não estiver na nossa lista atual (ou total aumentou)
          const isLatestNew = !allArticles.find(a => a._id === latestArticle._id);
          
          if (isLatestNew || currentTotal > myTotalCount) {
            setHasNewArticles(true);
          }
        }
      } catch (err) {
        console.error("Radar silencioso falhou:", err);
      }
    };

    // A cada 3 minutos (180000 ms)
    const interval = setInterval(checkForNewArticles, 3 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [allArticles, filters, sortBy, timePeriod, tagId, collectionId, data?.totalCount]);

  // Função manual para forçar recarregamento limpo quando o usuário clica no aviso
  const refreshCacheAndData = useCallback(() => {
    console.log('[CACHE DELETE] refreshCacheAndData called', sessionKey);
    globalArticlesMemoryCache.delete(sessionKey);
    const scrollKey = `m_scroll_${window.location.pathname}`;
    sessionStorage.removeItem(scrollKey);
    setPage(1);
    setHasNewArticles(false);
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.articles] });
    refetch();
  }, [sessionKey, queryClient, refetch]);

  // Escutar evento global de recálculo de tier para invalidar cache automaticamente.
  // Quando TierManagement conclui um sync/recálculo, dispara 'tier:recalculated'
  // e a lista de artigos é automaticamente recarregada sem precisar de Ctrl+Shift+R.
  useEffect(() => {
    const handleTierRecalculated = () => {
      console.log('[CACHE DELETE] tier:recalculated event received — invalidando cache de artigos');
      globalArticlesMemoryCache.delete(sessionKey);
      setPage(1);
      setAllArticles([]);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.articles] });
    };
    window.addEventListener('tier:recalculated', handleTierRecalculated);
    return () => window.removeEventListener('tier:recalculated', handleTierRecalculated);
  }, [sessionKey, queryClient]);

  const mutations = useArticleMutations();

  // Prefetching da próxima página
  const { prefetchNextPage } = usePrefetchNextPage({
    page,
    limit: 60,
    sort: sortBy,
    search: filters.search,
    sentiment: filters.sentiment,
    exposure: filters.exposure,
    isRead: filters.isRead,
    isFavorite: filters.isFavorite,
    tags: filters.tags,
    sources: filters.sources,
    mediaTypes: filters.mediaTypes,
    contentTypes: filters.contentTypes,
    entityType: filters.entityType,
    timePeriod,
    startDate: filters.startDate,
    endDate: filters.endDate,
    dateType: filters.dateType,
    tagId,
    collectionId,
    tiers: filters.tier || filters.tiers,
    editorialOccupancyStatus: filters.editorialOccupancyStatus,
    qualitativeAnalysisStatus: filters.qualitativeAnalysisStatus,
  });

  // Atualizar allArticles quando data mudar
  // IMPORTANTE: brandVersion na dependência força reset quando marca muda
  useEffect(() => {
    if (data?.data) {
      if (page === 1) {
        setAllArticles(data.data);
      } else {
        setAllArticles(prev => {
          const existingIds = new Set(prev.map(a => a._id));
          const newArticles = data.data.filter(a => !existingIds.has(a._id));
          return [...prev, ...newArticles];
        });
      }
    }
  }, [data, page, brandVersion]);

  // Sincroniza o cache global sempre que os artigos ou a página mudam
  useEffect(() => {
    if (allArticles.length > 0) {
      globalArticlesMemoryCache.set(sessionKey, {
        articles: allArticles,
        page,
        timestamp: Date.now()
      });
    }
    // Removido: delete(sessionKey) quando length === 0
    // Isso causava a perda de cache durante transições de rota onde allArticles era limpo.
  }, [allArticles, page, sessionKey]);

  // Resetar artigos quando a sessionKey muda (ex: brandVersion mudou)
  // Mas NÃO no mount inicial!
  const prevSessionKey = useRef(sessionKey);
  useEffect(() => {
    if (sessionKey !== prevSessionKey.current) {
      console.log('[CACHE RESET] sessionKey changed from', prevSessionKey.current, 'to', sessionKey);
      setAllArticles([]);
      setPage(1);
      prevSessionKey.current = sessionKey;
    }
  }, [sessionKey]);

  // Prefetch automático quando scrollar 80%
  useEffect(() => {
    if (data?.data?.length === 60) {
      // Se retornou 60 itens, provavelmente tem mais
      prefetchNextPage();
    }
  }, [data, prefetchNextPage]);

  // Handlers compatíveis com a tela de listagem
  const handleToggleFavorite = useCallback(async (articleId) => {
    // Optimistic update direto na lista acumulada
    setAllArticles(prev => prev.map(a => 
      a._id === articleId ? { ...a, isFavorite: !a.isFavorite } : a
    ));
    mutations.toggleFavorite.mutate(articleId);
  }, [mutations.toggleFavorite]);

  const handleToggleRead = useCallback(async (articleId) => {
    // Optimistic update direto na lista acumulada
    setAllArticles(prev => prev.map(a => 
      a._id === articleId ? { ...a, isRead: !a.isRead } : a
    ));
    mutations.toggleRead.mutate(articleId);
  }, [mutations.toggleRead]);

  const handleUpdateSentiment = useCallback(async (articleId, sentiment) => {
    // Optimistic update direto na lista acumulada
    setAllArticles(prev => prev.map(a => 
      a._id === articleId ? { ...a, sentiment, sentimentManual: true } : a
    ));
    mutations.updateSentiment.mutate({ articleId, sentiment });
  }, [mutations.updateSentiment]);

  const refreshArticles = useCallback(() => {
    refetch();
  }, [refetch]);

  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const loadMore = useCallback(() => {
    if (!isLoading && data?.data?.length === 60) {
      setPage(prev => prev + 1);
    }
  }, [isLoading, data]);

  const applyFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setPage(1);
    setAllArticles([]); // Limpar imediatamente
    globalArticlesMemoryCache.delete(sessionKey);
  }, [sessionKey]);

  const handleFilterChange = useCallback((newFilters) => {
    // Salvar a posição atual do scroll ANTES de limpar o cache
    const scrollEl = document.querySelector('.page-layout__content');
    const scrollKey = `m_scroll_${window.location.pathname}`;
    if (scrollEl && scrollEl.scrollTop > 0) {
      sessionStorage.setItem(scrollKey, scrollEl.scrollTop);
    }

    setPage(1);
    setFilters(newFilters);
    globalArticlesMemoryCache.delete(sessionKey);

    // Não esvaziar a lista antes da resposta. Quando a requisição demora ou
    // falha, zerar allArticles deixava a tela presa visualmente no carregamento
    // ("0 de N") até o timeout/retry do Axios. A resposta do novo queryKey
    // substitui os artigos normalmente no effect acima.

    // Persistir datas personalizadas no localStorage
    if (newFilters.startDate) {
      localStorage.setItem('article_startDate', newFilters.startDate.toISOString());
    } else {
      localStorage.removeItem('article_startDate');
    }

    if (newFilters.endDate) {
      localStorage.setItem('article_endDate', newFilters.endDate.toISOString());
    } else {
      localStorage.removeItem('article_endDate');
    }
  }, [sessionKey]);

  // Tags management para a tela de listagem
  const handleAddTags = useCallback(async (articleId, newTags) => {
    try {
      const article = allArticles.find(a => a._id === articleId);
      if (!article) return null;

      const currentTags = article.tags || [];
      const allTagIds = [...currentTags.map(t => t._id), ...newTags.map(t => t._id || t)];

      await api.patch(`/articles/${articleId}/tags`, {
        tags: allTagIds
      });

      // BUSCAR O ARTIGO COMPLETO AGORA para ter as tags populadas
      const updatedArticleRes = await api.get(`/articles/${articleId}`);
      const updatedArticle = updatedArticleRes.data.data;

      setAllArticles(prev =>
        prev.map(a => a._id === articleId ? updatedArticle : a)
      );

      await fetchGlobalTags();
      return updatedArticle;
    } catch (err) {
      console.error('Erro ao adicionar tags:', err);
      console.error('Detalhes do erro:', err.response?.data);
      return null;
    }
  }, [allArticles, fetchGlobalTags]);

  const handleRemoveTag = useCallback(async (articleId, tagId) => {
    try {
      const article = allArticles.find(a => a._id === articleId);
      if (!article) return null;

      const updatedTagIds = (article.tags || [])
        .filter(tag => tag._id !== tagId)
        .map(tag => tag._id);

      await api.patch(`/articles/${articleId}/tags`, {
        tags: updatedTagIds
      });

      // BUSCAR O ARTIGO COMPLETO AGORA para ter as tags populadas (com nome, cor, etc)
      // Isso corrige o bug onde a resposta do PATCH retornava apenas IDs
      const updatedArticleRes = await api.get(`/articles/${articleId}`);
      const updatedArticle = updatedArticleRes.data.data;

      setAllArticles(prev =>
        prev.map(a => a._id === articleId ? updatedArticle : a)
      );

      await fetchGlobalTags();
      return updatedArticle;
    } catch (err) {
      console.error('Erro ao remover tag:', err);
      console.error('Detalhes do erro:', err.response?.data);
      return null;
    }
  }, [allArticles, fetchGlobalTags]);

  // Buscar opções de fontes (sources)
  const fetchAllSourceOptions = useCallback(async () => {
    try {
      const response = await api.get('/articles/sources');
      if (response.data && Array.isArray(response.data.data)) {
        setAllSourceOptions(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao buscar fontes:', error);
      setAllSourceOptions([]);
    }
  }, []);

  // Carregar fontes ao montar
  useEffect(() => {
    fetchAllSourceOptions();
  }, [fetchAllSourceOptions]);


  // CRUD operations (mantém compatibilidade)
  const {
    deleteArticle: deleteArticleFromApi,
    updateArticleInState,
    removeArticleFromState
  } = useArticleCRUD(
    setAllArticles,
    (msg, sev) => console.log(msg), // TODO: integrar com snackbar
    null
  );

  const deleteArticle = useCallback(async (articleId) => {
    const deleted = await deleteArticleFromApi(articleId);

    if (deleted) {
      removeArticlesFromCache(articleId);
    }

    return deleted;
  }, [deleteArticleFromApi, removeArticlesFromCache]);

  // Força re-busca de artigos (útil quando dados externos mudam, ex: tags de coleção)
  const forceRefresh = useCallback(() => {
    console.log('[CACHE DELETE] forceRefresh called', sessionKey);
    setAllArticles([]); // Limpa cache local
    setExternalRefreshKey(k => k + 1); // Força re-fetch
    setPage(1);
    globalArticlesMemoryCache.delete(sessionKey);
  }, [sessionKey]);

  return {
    articles: allArticles,
    setArticles: setAllArticles,
    tags: contextTags,
    // isFetching também cobre a troca de filtros enquanto os resultados
    // anteriores permanecem visíveis até a nova resposta chegar.
    loading: isLoading || isFetching,
    error: error?.message || null,
    page,
    totalPages: data?.total ? Math.ceil(data.total / 60) : 1,
    totalCount: data?.total || 0,
    timePeriod,
    setTimePeriod,
    filters,
    handlePageChange,
    handleToggleFavorite,
    handleToggleRead,
    handleUpdateSentiment,
    refreshArticles,
    refreshTags: fetchGlobalTags,
    sortBy,
    setSortBy,
    hasMore: data?.data?.length === 60,
    loadMore,
    applyFilters,
    handleFilterChange,
    handleAddTags,
    handleRemoveTag,
    deleteArticle,
    updateArticleInState,
    removeArticleFromState,
    removeArticlesFromCache,
    allSourceOptions,
    forceRefresh,
    // Como a restauração de memória é síncrona e instantânea, isRestoringSession nunca será true
    isRestoringSession: false,

    // Extras do React Query
    isRefetching: isLoading,
    isCached: data?.isCached || false,
    hasNewArticles,
    refreshCacheAndData
  };
};

export default useArticleFetcherWithCache;
